import { FlexContainer } from '@/ui/styled-ui';
import {
  bigPadding,
  colorBlackMain,
  colorPurpleText,
  colorRedLight,
  colorRedMain,
  headingSmallFontSize,
  smallPadding,
  xSmallFontSize,
} from '@/utils/helpers/css-variables.helper';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart } from 'primereact/chart';
import React, { useEffect, useState } from 'react';
import {
  createBarChartTooltip,
  hideTooltip,
  showTooltip,
  tooltipDefaultSettings,
} from '../charts.helper';

export type BarChartProps = {
  id: string;
  value: number;
  secondaryValue: number;
  width?: string;
  height?: string;
  displayXAxis?: boolean;
  tooltip?: string;
  defaultTooltip?: string;
  benchmarkTooltip?: string;
  maxValue?: number;
  isMainDashboard?: boolean;
  isDemo?: boolean;
  isPurple?: boolean;
  onHover?: (event: any, activeElements: any[]) => void;
  onClick?: (event: any, activeElements: any[]) => void;
};

export const BarChart: React.FC<BarChartProps> = ({
  id,
  value,
  secondaryValue,
  width = '180',
  height = 'auto',
  displayXAxis,
  tooltip,
  defaultTooltip,
  benchmarkTooltip,
  maxValue,
  isMainDashboard,
  isDemo,
  isPurple,
  onHover,
  onClick,
}) => {
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const docStyle = getComputedStyle(document.documentElement);

  useEffect(() => {
    const data = {
      labels: [id],
      datasets: [
        {
          backgroundColor: [
            isPurple ? colorPurpleText(docStyle) : colorRedMain(docStyle),
          ],
          data: [value],
        },
        ...(secondaryValue > 0
          ? [
              {
                backgroundColor: [
                  isPurple
                    ? `${colorPurpleText(docStyle)}66`
                    : colorRedLight(docStyle),
                ],
                data: [secondaryValue],
              },
            ]
          : []),
      ],
    };
    const options = {
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          color: [colorBlackMain(docStyle)],
          font: {
            weight: 'bold',
            size: isMainDashboard
              ? xSmallFontSize(docStyle)
              : headingSmallFontSize(docStyle),
          },
          anchor: 'end',
          align: 'top',
          display: (context: any) => {
            return context.dataset.data[context.dataIndex] > 0;
          },
          formatter: (value: any, context: any) => {
            return context.datasetIndex === 0 && isDemo && isMainDashboard
              ? '?'
              : value;
          },
        },
        tooltip: isMainDashboard
          ? false
          : {
              enabled: false,
              external: (context: any) => {
                const { chart, tooltip: tooltipContext } = context;
                const el = createBarChartTooltip(
                  `chartjs-tooltip-${id}`,
                  tooltipDefaultSettings(),
                );
                if (
                  !tooltipContext ||
                  tooltipContext.opacity === 0 ||
                  !tooltipContext.body?.length
                ) {
                  hideTooltip(el);
                  return;
                }
                const item = tooltipContext.dataPoints?.[0];
                const label =
                  item?.datasetIndex === 0
                    ? tooltip || defaultTooltip || ''
                    : benchmarkTooltip || '';
                label
                  ? showTooltip(el, label, chart, tooltipContext)
                  : hideTooltip(el);
              },
            },
      },
      min: 0,
      max: 100,
      barPercentage: isMainDashboard ? 0.7 : 0.6,
      categoryPercentage: isMainDashboard ? 1 : 0.8,
      padding: 0,
      scales: {
        x: {
          ticks: {
            display: false,
          },
          border: {
            display: displayXAxis,
          },
          grid: {
            display: false,
            drawBorder: false,
          },
        },
        y: {
          beginAtZero: true,
          max: maxValue,
          ticks: {
            display: false,
          },
          border: {
            display: false,
          },
          grid: {
            display: false,
            drawBorder: false,
          },
        },
      },
      layout: {
        padding: {
          top: isMainDashboard
            ? smallPadding(docStyle)?.replace('px', '')
            : bigPadding(docStyle)?.replace('px', ''),
          left: 0,
          right: 0,
        },
      },
      responsive: false,
      maintainAspectRatio: false,
      onHover: onHover,
      onClick: onClick,
    };

    setChartData(data);
    setChartOptions(options);
  }, [
    value,
    secondaryValue,
    tooltip,
    defaultTooltip,
    benchmarkTooltip,
    id,
    isMainDashboard,
    isDemo,
    isPurple,
    displayXAxis,
    maxValue,
    onHover,
    onClick,
  ]);

  return (
    <FlexContainer
      direction="column"
      width="min-content"
      className="canvas-wrapper"
    >
      <div className="demo-overlay">
        <Chart
          id={id}
          type="bar"
          data={chartData}
          options={chartOptions}
          plugins={[ChartDataLabels]}
          className="chart"
          width={width}
          height={height}
        />
      </div>
    </FlexContainer>
  );
};
